import { useCallback, useEffect } from 'react';

import { useLmsHomeworkRosterQuery } from 'core/graphql';
import type { LmsHomeworkRosterQuery } from 'core/graphql';

import { transformHomeworkRoster } from './transform';
import type { IHomeworkRosterRow } from './transform';

interface IUseHomeworkRoster {
  roster: IHomeworkRosterRow[];
  /**
   * Server-side HOMEWORK_AI_ENABLED. The whole AI pipeline is dark when this is
   * false, so the drawer hides the AI card and the re-run control rather than
   * offering a button that can only ever fail with AI_DISABLED.
   */
  aiEnabled: boolean;
  loading: boolean;
  error: string;
  refetch: () => void;
}

/** How often to re-check an AI review that is still running (ms). */
const AI_POLL_INTERVAL = 10000;

const RUNNING_AI_STATUSES = ['pending', 'fetching', 'analyzing'];

const hasRunningAiReview = (data?: LmsHomeworkRosterQuery): boolean =>
  (data?.homeworkRoster || []).some((row) =>
    RUNNING_AI_STATUSES.includes(row?.submission?.aiReview?.status || ''),
  );

/**
 * Teacher roster: every student of the lesson, submitted or not.
 * cache-and-network keeps AI statuses reasonably fresh on revisit; grading and
 * AI-rerun mutations refetch this operation by name ('LmsHomeworkRoster').
 *
 * NOT gated on the assignment existing: `homeworkRoster` serves lessons
 * without one (the old grid graded those too), so the roster — and therefore
 * grading — must stay reachable for every lesson.
 *
 * While any submission's AI review is still pending/fetching/analyzing the
 * query polls: the verdict lands from a background Celery task, so without
 * this the teacher would sit on a spinner that never resolves until they
 * navigate away and back. Polling stops as soon as every review has settled
 * (done/failed) AND whenever the tab is hidden — the AI queue can run for
 * hours, and a backgrounded tab must not keep hitting the server.
 */
export const useHomeworkRoster = (lessonId: string): IUseHomeworkRoster => {
  const { data, loading, error, refetch, startPolling, stopPolling } = useLmsHomeworkRosterQuery({
    variables: { lessonId },
    skip: !lessonId,
    fetchPolicy: 'cache-and-network',
  });

  const aiRunning = hasRunningAiReview(data);

  useEffect(() => {
    const sync = () => {
      if (aiRunning && !document.hidden) {
        startPolling(AI_POLL_INTERVAL);
      } else {
        stopPolling();
      }
    };
    const onVisibilityChange = () => {
      // Coming back: catch up immediately instead of waiting a poll tick.
      if (!document.hidden && aiRunning) {
        refetch();
      }
      sync();
    };

    sync();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopPolling();
    };
  }, [aiRunning, refetch, startPolling, stopPolling]);

  const refetchRoster = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    roster: transformHomeworkRoster(data),
    aiEnabled: Boolean(data?.homeworkAiEnabled),
    loading: loading && !data,
    error: error?.message || '',
    refetch: refetchRoster,
  };
};
